import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { TextField, Button, Paper } from '@mui/material';

const HomeownersSignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  // Add state for estimate preview
  const [savedEstimate, setSavedEstimate] = useState(null);

  // Load saved estimate on mount
  useEffect(() => {
    try {
      const savedEstimateData = localStorage.getItem('pendingEstimate');
      console.log('Raw saved estimate:', savedEstimateData); // Debug log

      if (savedEstimateData) {
        try {
          const parsedEstimate = JSON.parse(savedEstimateData);
          console.log('Parsed estimate:', parsedEstimate); // Debug log
          
          // Validate the parsed data
          if (parsedEstimate && parsedEstimate.amount && parsedEstimate.projectDetails) {
            setSavedEstimate(parsedEstimate);
          } else {
            console.error('Invalid estimate data structure:', parsedEstimate);
          }
        } catch (parseError) {
          console.error('Error parsing estimate:', parseError);
        }
      }
    } catch (error) {
      console.error('Error loading saved estimate:', error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 1. Create the account
      const signupResponse = await fetch('http://localhost:5001/api/homeowners/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const signupData = await signupResponse.json();

      if (!signupResponse.ok) {
        throw new Error(signupData.message || 'Failed to create account');
      }

      // 2. Get the pending estimate
      const pendingEstimate = JSON.parse(localStorage.getItem('pendingEstimate'));
      
      if (pendingEstimate) {
        console.log('Saving estimate for new user:', signupData.userId);
        
        // 3. Save the estimate for the new user
        const estimateResponse = await fetch('http://localhost:5001/api/estimates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${signupData.token}`
          },
          body: JSON.stringify({
            userId: signupData.userId,
            amount: pendingEstimate.amount,
            projectDetails: pendingEstimate.projectDetails
          })
        });

        if (!estimateResponse.ok) {
          console.error('Failed to save estimate:', await estimateResponse.json());
        }

        // Clear the pending estimate
        localStorage.removeItem('pendingEstimate');
      }

      // Store auth token and user ID
      localStorage.setItem('authToken', signupData.token);
      localStorage.setItem('userId', signupData.userId);

      // Navigate to dashboard
      navigate('/dashboard');

    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <Container>
      <ContentWrapper>
        {/* Estimate Preview on the left */}
        {savedEstimate && savedEstimate.projectDetails && (
          <EstimatePreview>
            <PreviewTitle>Your Saved Estimate</PreviewTitle>
            <Amount>${parseFloat(savedEstimate.amount || 0).toLocaleString()}</Amount>
            <Details>
              {savedEstimate.projectDetails.service && (
                <DetailRow>
                  <DetailLabel>Service:</DetailLabel>
                  <DetailValue>
                    {savedEstimate.projectDetails.service.split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </DetailValue>
                </DetailRow>
              )}
              
              {savedEstimate.projectDetails.dimensions && (
                <DetailRow>
                  <DetailLabel>Dimensions:</DetailLabel>
                  <DetailValue>{savedEstimate.projectDetails.dimensions}</DetailValue>
                </DetailRow>
              )}
              
              {savedEstimate.projectDetails.area && (
                <DetailRow>
                  <DetailLabel>Area:</DetailLabel>
                  <DetailValue>{savedEstimate.projectDetails.area}</DetailValue>
                </DetailRow>
              )}
              
              {savedEstimate.projectDetails.zipCode && (
                <DetailRow>
                  <DetailLabel>Location:</DetailLabel>
                  <DetailValue>ZIP: {savedEstimate.projectDetails.zipCode}</DetailValue>
                </DetailRow>
              )}
            </Details>
          </EstimatePreview>
        )}

        {/* Signup Form on the right */}
        <SignupFormContainer>
          <StyledPaper>
            <Title>Create Your Account</Title>
            
            {/* Add Estimate Preview Section with proper checks */}
            {location.search.includes('from=calculator') && (
              <SavedEstimate>
                Your estimate will be saved to your account
              </SavedEstimate>
            )}
            
            <Form onSubmit={handleSubmit}>
              {/* Email - Full width */}
              <div className="full-width">
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </div>

              {/* Password fields - Side by side */}
              <TextField
                name="password"
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
              />

              {/* Name fields - Side by side */}
              <TextField
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required
                fullWidth
              />

              {/* Phone - Full width */}
              <div className="full-width">
                <TextField
                  name="phone"
                  label="Phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </div>

              {/* Address fields */}
              <div className="full-width">
                <TextField
                  name="address.street"
                  label="Street Address"
                  value={formData.address.street}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </div>

              {/* City and State - Side by side */}
              <TextField
                name="address.city"
                label="City"
                value={formData.address.city}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                name="address.state"
                label="State"
                value={formData.address.state}
                onChange={handleChange}
                required
                fullWidth
              />

              {/* ZIP Code - Full width */}
              <div className="full-width">
                <TextField
                  name="address.zipCode"
                  label="ZIP Code"
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </div>

              {error && (
                <div className="full-width">
                  <Error>{error}</Error>
                </div>
              )}

              <div className="full-width">
                <SubmitButton type="submit" variant="contained" fullWidth>
                  Create Account
                </SubmitButton>
              </div>
            </Form>
          </StyledPaper>
        </SignupFormContainer>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 4rem;
  width: 100%;
  max-width: 1200px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const EstimatePreview = styled.div`
  flex: 0 0 380px;
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  height: fit-content;
  margin: auto 0;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 420px;
  }
`;

const PreviewTitle = styled.h3`
  color: #2D3748;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #FF5722;
    border-radius: 2px;
  }
`;

const Amount = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #FF5722;
  text-align: center;
  margin: 2rem 0;
  padding: 1.5rem;
  background: #FFF5F2;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.1);
  border: 2px solid rgba(255, 87, 34, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 87, 34, 0.3);
    box-shadow: 0 6px 16px rgba(255, 87, 34, 0.15);
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.2rem;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    background: #f0f1f2;
    transform: translateX(4px);
    border-color: #e2e8f0;
  }
`;

const DetailLabel = styled.span`
  color: #4A5568;
  font-weight: 600;
  font-size: 0.95rem;
`;

const DetailValue = styled.span`
  color: #2D3748;
  font-weight: 700;
  font-size: 0.95rem;
  background: rgba(255, 87, 34, 0.1);
  padding: 0.3rem 0.8rem;
  border-radius: 8px;
`;

const SignupFormContainer = styled.div`
  flex: 1;
  max-width: 460px;
  margin: auto 0;
`;

const StyledPaper = styled(Paper)`
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  color: #2D3748;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 1.8rem;
  font-weight: 700;
`;

const SavedEstimate = styled.div`
  background: #EBF8FF;
  color: #2B6CB0;
  padding: 1rem 1.2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  .MuiTextField-root {
    margin-bottom: 0;
    
    .MuiOutlinedInput-root {
      border-radius: 10px;
      
      &:hover fieldset {
        border-color: #FF5722;
      }
    }
    
    .MuiOutlinedInput-input {
      padding: 0.8rem 1rem;
    }
  }

  // Full width items
  .full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Error = styled.div`
  color: #E53E3E;
  margin: 0.8rem 0;
  text-align: center;
  padding: 0.8rem 1rem;
  background: #FFF5F5;
  border-radius: 10px;
  font-size: 0.9rem;
`;

const SubmitButton = styled(Button)`
  && {
    background: #FF5722;
    padding: 1rem;
    margin-top: 1.5rem;
    font-size: 1rem;
    border-radius: 12px;
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.3px;
    box-shadow: 0 4px 12px rgba(255, 87, 34, 0.2);
    
    &:hover {
      background: #F4511E;
      box-shadow: 0 6px 16px rgba(255, 87, 34, 0.3);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

export default HomeownersSignUp;
